import React, { useState } from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircleOutline as CheckIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
} from '@mui/icons-material';

/**
 * Интерактивная схема Self-Retracting Lifeline (SRL).
 *
 * 7 номерованных pill-лейблов, curved pointer-lines, hover-подсветка
 * с привязкой к секциям inspection-чеклиста ниже на форме.
 * При hover на части (или клик на pill) — info-card справа показывает
 * описание + что проверять + соответствующую секцию чеклиста.
 */

// Цвета согласованы с INSPECTION_SECTIONS из constants.js — чтобы
// цвет на диаграмме соответствовал цвету секции чеклиста.
const SECTION_COLORS = {
    labelsMarkings: '#2e7d32',  // green
    housing:        '#4a148c',  // purple
    shockPack:      '#e65100',  // orange
    lifeline:       '#01579b',  // blue
    connectors:     '#b71c1c',  // red
};

const PARTS = [
    {
        id: 'attachment', n: 1,
        label: 'Attachment Point',
        section: 'housing',
        color: SECTION_COLORS.housing,
        // Точка на устройстве (anchor), к которой ведёт указатель
        anchor: { x: 360, y: 70 },
        // Pill располагается справа сверху
        pill:   { x: 575, y: 56,  side: 'right' },
        whatToCheck: [
            'Cracks, deformation or sharp bends in the eyelet',
            'Free swivel rotation — no binding',
            'Anchor pin is fully seated and secure',
        ],
    },
    {
        id: 'housing', n: 2,
        label: 'Housing',
        section: 'housing',
        color: SECTION_COLORS.housing,
        anchor: { x: 305, y: 220 },
        pill:   { x: 25,  y: 165, side: 'left' },
        whatToCheck: [
            'No dents, cracks, or visible rust',
            'All labels intact and legible',
            'No gaps between halves of the shell',
        ],
    },
    {
        id: 'screws', n: 3,
        label: 'Screws',
        section: 'housing',
        color: SECTION_COLORS.housing,
        anchor: { x: 415, y: 175 },
        pill:   { x: 575, y: 165, side: 'right' },
        whatToCheck: [
            'All fasteners present, none missing',
            'Tight — no movement when probed',
            'No corrosion on heads or threads',
        ],
    },
    {
        id: 'termination', n: 4,
        label: 'Termination',
        sublabel: 'Swage / Stitching',
        section: 'lifeline',
        color: SECTION_COLORS.lifeline,
        anchor: { x: 360, y: 360 },
        pill:   { x: 25,  y: 340, side: 'left' },
        whatToCheck: [
            'Swage ferrule firmly compressed, no slip',
            'Stitching intact — no broken threads',
            'No fraying where lifeline enters housing',
        ],
    },
    {
        id: 'shockPack', n: 5,
        label: 'Shock Pack',
        sublabel: '(if present)',
        section: 'shockPack',
        color: SECTION_COLORS.shockPack,
        anchor: { x: 380, y: 500 },
        pill:   { x: 575, y: 480, side: 'right' },
        whatToCheck: [
            'Outer cover (shrink tube) intact — DO NOT cut or remove',
            'No fraying, broken stitching, or visible damage',
            'No signs of impact deployment (extended packs)',
        ],
    },
    {
        id: 'impact', n: 6,
        label: 'Impact Indicator',
        section: 'connectors',
        color: SECTION_COLORS.connectors,
        anchor: { x: 380, y: 645 },
        pill:   { x: 575, y: 635, side: 'right' },
        whatToCheck: [
            'Indicator window NOT showing red / "CHECK"',
            'No deployment marks or expanded gauge',
            'If triggered → retire device immediately',
        ],
    },
    {
        id: 'connector', n: 7,
        label: 'Connector',
        sublabel: 'Snap hook / Carabiner',
        section: 'connectors',
        color: SECTION_COLORS.connectors,
        anchor: { x: 350, y: 760 },
        pill:   { x: 25,  y: 755, side: 'left' },
        whatToCheck: [
            'Self-closing AND self-locking gate',
            'No nicks, pitting, or corrosion on hook body',
            'Keeper / locking sleeve operates smoothly',
            'No visible deformation or wear at load points',
        ],
    },
];

const SECTION_LABELS = {
    housing:        'Housing',
    shockPack:      'Shock Pack',
    lifeline:       'Lifeline / Web',
    connectors:     'Connectors',
    labelsMarkings: 'Labels & Markings',
};

const SrdDeviceDiagram = () => {
    const [hovered, setHovered] = useState(null);
    const [pinned, setPinned] = useState(null);

    const active = pinned || hovered;
    const activePart = active ? PARTS.find(p => p.id === active) : null;

    const isHi = (id) => active === id;
    const isDimmed = (id) => active !== null && active !== id;

    /** Кубическая кривая Безье от anchor к pill */
    const curveTo = (a, p) => {
        const cx1 = a.x + (p.side === 'right' ? 80 : -80);
        const cy1 = a.y;
        const cx2 = p.x + (p.side === 'right' ? -40 : 40);
        const cy2 = p.y + 18;
        const tx = p.x + (p.side === 'right' ? 0 : 165);
        const ty = p.y + 18;
        return `M ${a.x} ${a.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${tx} ${ty}`;
    };

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 300px' },
            gap: 2,
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%)',
            borderRadius: 2,
            p: { xs: 1, sm: 1.5 },
            border: '1px solid #e0e6ed',
        }}>
            {/* ============= LEFT: SVG diagram ============= */}
            <Box sx={{
                position: 'relative',
                display: 'flex', justifyContent: 'center',
            }}>
                <svg viewBox="0 0 760 820" style={{
                    width: '100%',
                    maxWidth: 560,
                    maxHeight: '70vh',
                    height: 'auto',
                    display: 'block',
                }}>
                    <defs>
                        <linearGradient id="metal2" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%"   stopColor="#9e9e9e" />
                            <stop offset="50%"  stopColor="#f5f5f5" />
                            <stop offset="100%" stopColor="#616161" />
                        </linearGradient>
                        <linearGradient id="housingGrad2" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%"   stopColor="#1a1a1a" />
                            <stop offset="50%"  stopColor="#3d3d3d" />
                            <stop offset="100%" stopColor="#0a0a0a" />
                        </linearGradient>
                        <linearGradient id="shockGrad2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor="#1976d2" />
                            <stop offset="100%" stopColor="#0d47a1" />
                        </linearGradient>
                        <linearGradient id="webGrad2" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%"   stopColor="#1a1a1a" />
                            <stop offset="50%"  stopColor="#3a3a3a" />
                            <stop offset="100%" stopColor="#1a1a1a" />
                        </linearGradient>
                        <filter id="dropShadow2" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="3" stdDeviation="3" floodOpacity="0.3" />
                        </filter>
                        <filter id="pillShadow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.18" />
                        </filter>
                    </defs>

                    {/* ===================== DEVICE ===================== */}

                    {/* 1. Attachment Point */}
                    <motion.g
                        onMouseEnter={() => setHovered('attachment')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'attachment' ? null : 'attachment')}
                        style={{ cursor: 'pointer', opacity: isDimmed('attachment') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('attachment') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.05, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <ellipse cx="360" cy="48"  rx="16" ry="13" fill="none" stroke="url(#metal2)" strokeWidth="6" />
                        <rect x="352" y="58"  width="16" height="14" rx="2" fill="url(#metal2)" />
                        <rect x="344" y="70"  width="32" height="18" rx="2.5" fill="#bdbdbd" stroke="#424242" strokeWidth="0.8" />
                        {isHi('attachment') && (
                            <motion.ellipse cx="360" cy="65" rx="36" ry="30" fill="none"
                                stroke={PARTS[0].color} strokeWidth="3"
                                initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.25 }} strokeDasharray="6 4" />
                        )}
                    </motion.g>

                    {/* 2. Housing */}
                    <motion.g
                        onMouseEnter={() => setHovered('housing')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'housing' ? null : 'housing')}
                        style={{ cursor: 'pointer', opacity: isDimmed('housing') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('housing') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.12, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <path
                            d="M 285 105 Q 285 92 305 92 L 415 92 Q 435 92 435 105 L 442 322 Q 442 348 418 348 L 302 348 Q 278 348 278 322 Z"
                            fill="url(#housingGrad2)" stroke="#000" strokeWidth="1.4"
                        />
                        {/* HALO badge */}
                        <rect x="313" y="148" width="94" height="50" rx="3" fill="#0a0a0a" stroke="#3a3a3a" strokeWidth="0.7" />
                        <text x="360" y="180" textAnchor="middle" fontFamily="Arial Black, sans-serif"
                              fontSize="24" fontWeight="900" fill="#fafafa" letterSpacing="2.5">HALO</text>
                        {/* Cert sticker */}
                        <rect x="318" y="248" width="84" height="30" rx="2.5" fill="#e8e8e8" stroke="#9e9e9e" strokeWidth="0.6" />
                        <text x="360" y="261" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="7" fill="#424242">ANSI Z359 / OSHA 1926</text>
                        <text x="360" y="270" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="6" fill="#616161">SN 0012159 · Mfd 2024</text>
                        {/* Highlight band */}
                        <path d="M 285 105 Q 285 92 305 92 L 415 92 Q 435 92 435 105 L 437 130 L 283 130 Z" fill="rgba(255,255,255,0.06)" />
                        {isHi('housing') && (
                            <motion.rect x="270" y="84" width="180" height="272" rx="14" fill="none"
                                stroke={PARTS[1].color} strokeWidth="3" strokeDasharray="8 5"
                                initial={{ opacity: 0 }} animate={{ opacity: 0.95 }} transition={{ duration: 0.25 }} />
                        )}
                    </motion.g>

                    {/* 3. Screws (4 corners) */}
                    <motion.g
                        onMouseEnter={() => setHovered('screws')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'screws' ? null : 'screws')}
                        style={{ cursor: 'pointer', opacity: isDimmed('screws') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0 }} animate={{ opacity: isDimmed('screws') ? 0.35 : 1 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                    >
                        {[
                            { cx: 305, cy: 130 }, { cx: 415, cy: 130 },
                            { cx: 300, cy: 312 }, { cx: 420, cy: 312 },
                        ].map((p, i) => (
                            <g key={i}>
                                <circle cx={p.cx} cy={p.cy} r="6" fill="#bdbdbd" stroke="#424242" strokeWidth="0.9" />
                                <line x1={p.cx-3.5} y1={p.cy} x2={p.cx+3.5} y2={p.cy} stroke="#1a1a1a" strokeWidth="1.3" />
                                {isHi('screws') && (
                                    <motion.circle cx={p.cx} cy={p.cy} r="11" fill="none"
                                        stroke={PARTS[2].color} strokeWidth="2"
                                        initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.25, delay: i * 0.04 }} />
                                )}
                            </g>
                        ))}
                    </motion.g>

                    {/* 4. Termination */}
                    <motion.g
                        onMouseEnter={() => setHovered('termination')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'termination' ? null : 'termination')}
                        style={{ cursor: 'pointer', opacity: isDimmed('termination') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('termination') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.28, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <rect x="352" y="348" width="16" height="22" fill="url(#webGrad2)" />
                        <rect x="340" y="370" width="40" height="26" rx="3" fill="url(#metal2)" stroke="#424242" strokeWidth="1" />
                        {[376, 382, 388].map(y => (
                            <line key={y} x1="344" y1={y} x2="376" y2={y} stroke="#424242" strokeWidth="0.6" strokeDasharray="2.5 1.2" />
                        ))}
                        {isHi('termination') && (
                            <motion.rect x="332" y="362" width="56" height="42" rx="4" fill="none"
                                stroke={PARTS[3].color} strokeWidth="3" strokeDasharray="6 4"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }} />
                        )}
                    </motion.g>

                    {/* 5. Shock Pack */}
                    <motion.g
                        onMouseEnter={() => setHovered('shockPack')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'shockPack' ? null : 'shockPack')}
                        style={{ cursor: 'pointer', opacity: isDimmed('shockPack') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('shockPack') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.36, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <rect x="332" y="404" width="56" height="222" rx="7" fill="url(#shockGrad2)" stroke="#0a3672" strokeWidth="1.2" />
                        {[0,1,2,3,4,5,6,7].map(i => (
                            <line key={i} x1="338" y1={420 + i*26} x2="382" y2={420 + i*26}
                                  stroke="#0d47a1" strokeWidth="0.7" opacity="0.55" />
                        ))}
                        <line x1="332" y1="416" x2="388" y2="416" stroke="#ff9800" strokeWidth="1.4" strokeDasharray="3.5 2" />
                        <line x1="332" y1="618" x2="388" y2="618" stroke="#ff9800" strokeWidth="1.4" strokeDasharray="3.5 2" />
                        <rect x="338" y="404" width="7" height="222" rx="3" fill="rgba(255,255,255,0.10)" />
                        {isHi('shockPack') && (
                            <motion.rect x="324" y="396" width="72" height="238" rx="11" fill="none"
                                stroke={PARTS[4].color} strokeWidth="3" strokeDasharray="8 5"
                                initial={{ opacity: 0 }} animate={{ opacity: 0.95 }} transition={{ duration: 0.25 }} />
                        )}
                    </motion.g>

                    {/* 6. Impact Indicator */}
                    <motion.g
                        onMouseEnter={() => setHovered('impact')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'impact' ? null : 'impact')}
                        style={{ cursor: 'pointer', opacity: isDimmed('impact') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('impact') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.44, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <rect x="352" y="626" width="16" height="32" fill="url(#webGrad2)" />
                        <rect x="335" y="652" width="50" height="22" rx="3.5" fill="#fff" stroke="#424242" strokeWidth="1" />
                        <rect x="338" y="655" width="44" height="16" rx="2" fill="#e53935" />
                        <text x="360" y="667" textAnchor="middle" fontFamily="Arial Black, sans-serif"
                              fontSize="9" fontWeight="900" fill="#fff" letterSpacing="0.8">CHECK</text>
                        {isHi('impact') && (
                            <motion.rect x="328" y="646" width="64" height="34" rx="5" fill="none"
                                stroke={PARTS[5].color} strokeWidth="3" strokeDasharray="6 4"
                                initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.25 }} />
                        )}
                    </motion.g>

                    {/* 7. Connector */}
                    <motion.g
                        onMouseEnter={() => setHovered('connector')} onMouseLeave={() => setHovered(null)}
                        onClick={() => setPinned(pinned === 'connector' ? null : 'connector')}
                        style={{ cursor: 'pointer', opacity: isDimmed('connector') ? 0.35 : 1, transition: 'opacity .25s' }}
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: isDimmed('connector') ? 0.35 : 1, y: 0 }}
                        transition={{ delay: 0.52, duration: 0.4 }}
                        filter="url(#dropShadow2)"
                    >
                        <rect x="352" y="674" width="16" height="22" fill="url(#webGrad2)" />
                        <ellipse cx="360" cy="708" rx="13" ry="10" fill="none" stroke="url(#metal2)" strokeWidth="6" />
                        <path
                            d="M 360 720 L 360 790 Q 360 802 372 802 L 408 802 Q 420 802 420 790 L 420 752 L 410 752 L 410 786 Q 410 794 402 794 L 374 794 Q 366 794 366 782 L 366 720 Z"
                            fill="url(#metal2)" stroke="#424242" strokeWidth="1.2"
                        />
                        <line x1="376" y1="722" x2="412" y2="788" stroke="#616161" strokeWidth="2.6" strokeLinecap="round" />
                        <circle cx="412" cy="788" r="3" fill="#9e9e9e" stroke="#424242" strokeWidth="0.8" />
                        <rect x="375" y="740" width="11" height="26" rx="2.5" fill="#757575" stroke="#424242" strokeWidth="0.7" />
                        {isHi('connector') && (
                            <motion.rect x="345" y="698" width="92" height="116" rx="12" fill="none"
                                stroke={PARTS[6].color} strokeWidth="3" strokeDasharray="8 5"
                                initial={{ opacity: 0 }} animate={{ opacity: 0.95 }} transition={{ duration: 0.25 }} />
                        )}
                    </motion.g>

                    {/* ===================== POINTER LINES ===================== */}
                    {PARTS.map((p, i) => {
                        const d = curveTo(p.anchor, p.pill);
                        return (
                            <motion.path
                                key={p.id}
                                d={d}
                                fill="none"
                                stroke={isHi(p.id) ? p.color : '#90a4ae'}
                                strokeWidth={isHi(p.id) ? 2.5 : 1.5}
                                strokeOpacity={isDimmed(p.id) ? 0.2 : (isHi(p.id) ? 0.95 : 0.55)}
                                strokeLinecap="round"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                transition={{ delay: 0.7 + i * 0.06, duration: 0.5, ease: 'easeOut' }}
                                style={{ transition: 'stroke .2s, stroke-width .2s' }}
                            />
                        );
                    })}

                    {/* Anchor dots */}
                    {PARTS.map(p => (
                        <circle key={p.id + '-dot'}
                            cx={p.anchor.x} cy={p.anchor.y}
                            r={isHi(p.id) ? 5.5 : 3.5}
                            fill={isHi(p.id) ? p.color : '#fff'}
                            stroke={p.color} strokeWidth={2}
                            opacity={isDimmed(p.id) ? 0.3 : 1}
                            style={{ transition: 'r .15s, fill .2s, opacity .2s' }}
                        />
                    ))}

                    {/* ===================== PILL LABELS ===================== */}
                    {PARTS.map((p, i) => {
                        const w = 165, h = 38;
                        const isActive = isHi(p.id);
                        return (
                            <motion.g key={'pill-' + p.id}
                                onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}
                                onClick={() => setPinned(pinned === p.id ? null : p.id)}
                                style={{ cursor: 'pointer' }}
                                initial={{ opacity: 0, x: p.pill.side === 'right' ? 14 : -14 }}
                                animate={{
                                    opacity: isDimmed(p.id) ? 0.45 : 1,
                                    x: 0,
                                }}
                                transition={{ delay: 0.85 + i * 0.06, duration: 0.4 }}
                                filter={isActive ? "url(#pillShadow)" : ""}
                            >
                                <motion.rect
                                    x={p.pill.x} y={p.pill.y}
                                    width={w} height={h} rx={19}
                                    fill={isActive ? p.color : '#ffffff'}
                                    stroke={p.color}
                                    strokeWidth={isActive ? 2 : 1.5}
                                    style={{ transition: 'fill .2s, stroke-width .2s' }}
                                />
                                {/* Number badge */}
                                <circle
                                    cx={p.pill.side === 'right' ? p.pill.x + 19 : p.pill.x + w - 19}
                                    cy={p.pill.y + h / 2}
                                    r={13}
                                    fill={p.color}
                                />
                                <text
                                    x={p.pill.side === 'right' ? p.pill.x + 19 : p.pill.x + w - 19}
                                    y={p.pill.y + h / 2 + 5}
                                    textAnchor="middle"
                                    fontFamily="Arial Black, sans-serif"
                                    fontSize={14} fontWeight={900}
                                    fill="#fff"
                                >{p.n}</text>
                                {/* Label text */}
                                <text
                                    x={p.pill.side === 'right' ? p.pill.x + 38 : p.pill.x + 12}
                                    y={p.pill.y + (p.sublabel ? 16 : 24)}
                                    fontFamily="Arial, sans-serif"
                                    fontSize={p.sublabel ? 12 : 13}
                                    fontWeight={700}
                                    fill={isActive ? '#fff' : p.color}
                                    style={{ transition: 'fill .2s' }}
                                >{p.label}</text>
                                {p.sublabel && (
                                    <text
                                        x={p.pill.side === 'right' ? p.pill.x + 38 : p.pill.x + 12}
                                        y={p.pill.y + 30}
                                        fontFamily="Arial, sans-serif"
                                        fontSize={9.5}
                                        fontWeight={500}
                                        fill={isActive ? 'rgba(255,255,255,0.85)' : '#78909c'}
                                        style={{ transition: 'fill .2s' }}
                                    >{p.sublabel}</text>
                                )}
                            </motion.g>
                        );
                    })}
                </svg>
            </Box>

            {/* ============= RIGHT: info card (sticky on desktop) ============= */}
            <Box sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: activePart ? activePart.color : '#e0e6ed',
                background: activePart
                    ? `linear-gradient(135deg, ${activePart.color}10 0%, #ffffff 70%)`
                    : '#ffffff',
                transition: 'border-color .3s, background .3s',
                minHeight: 280,
                position: { xs: 'static', sm: 'sticky' },
                top: { sm: 8 },
                alignSelf: { sm: 'flex-start' },
                maxHeight: { sm: 'calc(70vh + 16px)' },
                overflow: 'auto',
                display: 'flex', flexDirection: 'column',
            }}>
                <AnimatePresence mode="wait">
                    {activePart ? (
                        <motion.div
                            key={activePart.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.25 }}
                        >
                            {/* Header: number badge + title */}
                            <Stack direction="row" spacing={1.25} alignItems="center" mb={1}>
                                <Box sx={{
                                    width: 30, height: 30, borderRadius: '50%',
                                    background: activePart.color,
                                    color: '#fff',
                                    fontWeight: 900, fontSize: 14,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: `0 2px 6px ${activePart.color}55`,
                                }}>{activePart.n}</Box>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: activePart.color, lineHeight: 1.2 }}>
                                        {activePart.label}
                                    </Typography>
                                    {activePart.sublabel && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                            {activePart.sublabel}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    size="small"
                                    label={SECTION_LABELS[activePart.section]}
                                    sx={{
                                        ml: 'auto', height: 22,
                                        backgroundColor: activePart.color + '15',
                                        color: activePart.color, fontWeight: 600, fontSize: '0.7rem',
                                    }}
                                />
                            </Stack>

                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontWeight: 700, mb: 0.75 }}>
                                What to inspect
                            </Typography>
                            <Stack spacing={0.5}>
                                {activePart.whatToCheck.map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, x: -6 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 + idx * 0.05, duration: 0.25 }}
                                    >
                                        <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                            <CheckIcon sx={{ fontSize: 16, color: activePart.color, mt: '2px', flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
                                                {item}
                                            </Typography>
                                        </Stack>
                                    </motion.div>
                                ))}
                            </Stack>

                            {pinned && (
                                <Box sx={{ mt: 1.5, p: 0.75, borderRadius: 1, background: '#fff8e1', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <WarningIcon sx={{ fontSize: 14, color: '#f57c00' }} />
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#5d4037' }}>
                                        Pinned. Click again on the part to release.
                                    </Typography>
                                </Box>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="placeholder"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <InfoIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                    SRL Anatomy Reference
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.55 }}>
                                Hover any numbered <b>pill</b> or any <b>part of the device</b> on the left to see what to check.
                                <br/><br/>
                                Click to <b>pin</b> a part — useful when reading off the inspection items below.
                            </Typography>

                            <Box sx={{ mt: 2, p: 1, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                    Color legend (matches checklist below)
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {Object.entries(SECTION_LABELS).filter(([k]) => k !== 'labelsMarkings').map(([k, v]) => (
                                        <Chip key={k} size="small" label={v}
                                            sx={{
                                                height: 20, fontSize: '0.65rem',
                                                backgroundColor: SECTION_COLORS[k] + '15',
                                                color: SECTION_COLORS[k], fontWeight: 600,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
        </Box>
    );
};

export default SrdDeviceDiagram;
